import React, { useEffect, useState, useMemo, useCallback } from "react";
import axios from "axios";
import { useLocation, Link } from "react-router-dom";
import { useTable, useSortBy, usePagination, useFilters, useRowSelect } from "react-table";
import DataNavigationModal from "../DataNavigationModal";
import FilterModal from "../FilterModal/FilterModal";
import "./StudyList.css";

const StudyList = ({ refreshData, onSelectedIdsChange }) => {
  const location = useLocation();
  const token = localStorage.getItem("token");
  const [studies, setStudies] = useState([]);
  const [pageCount, setPageCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setModalOpen] = useState(false);
  const [modalResourceId, setModalResourceId] = useState(null);
  const [isFilterModalOpen, setFilterModalOpen] = useState(false);
  const [placeholder, setPlaceholder] = useState("");

  const openModalForStudy = (study) => {
    setModalResourceId({
      studyId: study.id,
      patientId: study.patient,
    });
    setModalOpen(true);
  };

  const IndeterminateCheckbox = React.forwardRef(
    ({ indeterminate, checked, onChange, row, ...rest }, ref) => {
      const defaultRef = React.useRef();
      const resolvedRef = ref || defaultRef;

      React.useEffect(() => {
        resolvedRef.current.indeterminate = indeterminate;
      }, [resolvedRef, indeterminate]);

      return (
        <input 
          type="checkbox" 
          ref={resolvedRef} 
          checked={checked}
          onChange={(e) => {
            onChange(e);
            
            if (row && e.target.checked) {
              const study = studies[row.index];
              if (study && onSelectedIdsChange) {
                const selected = page
                  .filter(row => row.isSelected)
                  .map(row => studies[row.index].id)
                  .filter(id => id);
                
                onSelectedIdsChange(selected);
              }
            } else if (row && !e.target.checked) {
              const study = studies[row.index];
              if (study && onSelectedIdsChange) {
                const selected = page
                  .filter(row => row.isSelected)
                  .map(row => studies[row.index].id)
                  .filter(id => id);
                
                onSelectedIdsChange(selected);
              }
            } else if (!row) {
              if (e.target.checked) {
                const allIds = page
                  .map(row => studies[row.index].id)
                  .filter(id => id);
                
                onSelectedIdsChange(allIds);
              } else {
                onSelectedIdsChange([]);
              }
            }
          }}
          {...rest} 
        />
      );
    }
  );

  const columns = useMemo(
    () => [
      {
        Header: "ID",
        accessor: "id",
        filterType: "Exact"
      },
      {
        Header: "Study Instance ID",
        accessor: "study_instance_id",
      },
      {
        Header: "Patient",
        accessor: "patient",
        Cell: ({ value }) => <Link to={`/patients/${value}`}>{value}</Link>,
        filterType: "Exact"
      },
      {
        Header: "# of XA DICOMs",
        accessor: "dicom_count",
        Cell: ({ row }) => (
          <Link
            to={`/dicoms?patient=${row.original.patient}&study=${row.original.id}`}
          >
            {row.original.dicom_count}
          </Link>
        ),
        filterType: "Exact"
      },
      {
        Header: "Creation Time",
        accessor: "creation_time",
        Cell: ({ value }) => value !== null ? new Date(value).toLocaleString() : "N/A",
        filterType: "Exact"
      },
      {
        Header: "Updated Time",
        accessor: "updated_time",
        Cell: ({ value }) => value !== null ? new Date(value).toLocaleString() : "N/A",
        filterType: "Exact"
      },
      {
        Header: "Uploader",
        accessor: "uploader",
        filterType: "Exact"
      },
      {
        Header: "Actions",
        disableSortBy: true,
        disableFilters: true,
        Cell: ({ row }) => {
          const study = row.original;
          return (
            <div className="actions-container">
              <Link
                style={{ textDecoration: "none" }}
                to={`/studies/${study.id}`}
              >
                <button className="action-btn view">
                  <i className="fas fa-eye"></i> View
                </button>
              </Link>
              <Link
                style={{ textDecoration: "none" }}
                to={`/studies/${study.id}/edit`}
              >
                <button className="action-btn edit">
                  <i className="fas fa-edit"></i> Edit
                </button>
              </Link>
              <button
                className="action-btn data"
                onClick={() => openModalForStudy(study)}
              >
                <i className="fas fa-database"></i> Data
              </button>
            </div>
          );
        },
      },
    ],
    []
  );

  const defaultColumn = useMemo(() => ({}), []);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    canPreviousPage,
    canNextPage,
    pageOptions,
    nextPage,
    previousPage,
    setPageSize,
    setAllFilters,
    state,
    selectedFlatRows,
  } = useTable(
    {
      columns,
      data: studies,
      defaultColumn,
      manualPagination: true,
      manualSortBy: true,
      manualFilters: true,
      pageCount,
      initialState: {
        pageIndex: 0,
        pageSize: 10,
      },
    },
    useFilters,
    useSortBy,
    usePagination,
    useRowSelect,
    hooks => {
      hooks.visibleColumns.push(columns => [
        {
          id: 'selection',
          Header: ({ getToggleAllRowsSelectedProps }) => (
            <IndeterminateCheckbox {...getToggleAllRowsSelectedProps()} />
          ),
          Cell: ({ row }) => (
            <IndeterminateCheckbox {...row.getToggleRowSelectedProps()} row={row} />
          ),
        },
        ...columns,
      ]);
    }
  );

  const { pageIndex, pageSize, sortBy, filters } = state;

  const fetchStudies = useCallback(async () => {
    setLoading(true);
    const searchParams = new URLSearchParams(location.search);
    const patientId = searchParams.get("patient");

    let endpoint = `${process.env.REACT_APP_BACKEND_URL}/patients/studies/`;
    if (patientId) {
      endpoint += `?patient=${patientId}`;
    }
    try {
      let params = {
        page: pageIndex + 1,
        page_size: pageSize,
      };

      if (sortBy?.length > 0) {
        const { id, desc } = sortBy[0];
        params.sort_by = id;
        params.sort_dir = desc ? "desc" : "asc";
      }

      if (filters?.length) {
        filters.forEach(({ id, value }) => {
          if (value) {
            params[id] = value;
          }
        });
      }

      const res = await axios.get(endpoint, {
        params,
        headers: { Authorization: `Token ${token}` },
      });
      const { results, pageCount: serverPageCount } = res.data;

      setStudies(results || []);
      setPageCount(serverPageCount || 0);

      if (!results || !results.length) {
        setPlaceholder("No studies found.");
      } else {
        setPlaceholder("");
      }
    } catch (error) {
      console.error(error);
      setPlaceholder("Error fetching studies.");
    }
    setLoading(false);
  }, [pageIndex, pageSize, sortBy, filters, token, location.search]);

  useEffect(() => {
    fetchStudies();
  }, [fetchStudies, refreshData]);

  useEffect(() => {
    if (selectedFlatRows && onSelectedIdsChange) {
      const selectedIds = selectedFlatRows
        .map(row => studies[row.index]?.id)
        .filter(id => id);
      
      onSelectedIdsChange(selectedIds);
    }
  }, [selectedFlatRows, studies, onSelectedIdsChange]);

  if (loading) return <div>Loading studies...</div>;
  if (!page.length && placeholder) {
    return (
      <div className="placeholder-message">
        {placeholder}{" "}
        {filters.length > 0 && (
          <button className="filter-button" onClick={() => setAllFilters([])}>
            Clear Filters
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="study-list-container">
      <div className="table-actions">
        <button
          className="filter-button"
          onClick={() => setFilterModalOpen(true)}
        >
          Filter Data
        </button>
      </div>
      <div className="table-wrapper">
        <table {...getTableProps()} className="study-list-table">
          <thead>
            {headerGroups.map((headerGroup) => {
              const { key: headerGroupKey, ...headerGroupProps } = headerGroup.getHeaderGroupProps();
              return (
                <tr key={headerGroupKey} {...headerGroupProps}>
                  {headerGroup.headers.map((column) => {
                    const { key: colKey, ...colProps } = column.getHeaderProps();
                    return (
                      <th key={colKey} {...colProps}>
                        <div
                          {...(column.getSortByToggleProps
                            ? column.getSortByToggleProps()
                            : {})}
                        >
                          {column.render("Header")}
                          {column.isSorted ? (column.isSortedDesc ? " ▼" : " ▲") : ""}
                        </div>
                      </th>
                    );
                  })}
                </tr>
              );
            })}
          </thead>
          <tbody {...getTableBodyProps()}>
            {page.map((row) => {
              prepareRow(row);
              const { key: rowKey, ...rowProps } = row.getRowProps();
              return (
                <tr key={rowKey} {...rowProps}>
                  {row.cells.map((cell) => {
                    const { key: cellKey, ...cellProps } = cell.getCellProps();
                    return (
                      <td key={cellKey} {...cellProps}>
                        {cell.render("Cell")}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="pagination-container">
        <button onClick={() => previousPage()} disabled={!canPreviousPage}>
          Previous
        </button>
        <span>
          Page{" "}
          <strong>
            {pageIndex + 1} of {pageOptions.length}
          </strong>
        </span>
        <button onClick={() => nextPage()} disabled={!canNextPage}>
          Next
        </button>
        <select
          style={{ width: "auto" }}
          value={pageSize}
          onChange={(e) => setPageSize(Number(e.target.value))}
        >
          {[10, 20, 50, 100].map((size) => (
            <option key={size} value={size}>
              Show {size}
            </option>
          ))}
        </select>
      </div>

      <DataNavigationModal
        isOpen={isModalOpen}
        onClose={() => setModalOpen(false)}
        contextType="study"
        resourceIds={modalResourceId}
      />
      <FilterModal
        isOpen={isFilterModalOpen}
        onClose={() => setFilterModalOpen(false)}
        columns={columns.filter((col) => !col.disableFilters)}
        currentFilters={state.filters}
        onApplyFilters={(filters) => {
          setAllFilters(filters);
        }}
      />
    </div>
  );
};

export default StudyList;
